import { supabase } from '../lib/supabase';

export const followsService = {
  async followUser(userId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    
    if (user.id === userId) {
      throw new Error('Cannot follow yourself');
    }

    const { data, error } = await supabase
      .from('follows')
      .insert({
        follower_id: user.id,
        following_id: userId,
      })
      .select()
      .single();

    if (error) throw error;
    
    // Note: Notification will be created automatically by database trigger
    return data;
  },

  async unfollowUser(userId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('follows')
      .delete()
      .eq('follower_id', user.id)
      .eq('following_id', userId);

    if (error) throw error;
  },

  async isFollowing(userId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('follows')
      .select('id')
      .eq('follower_id', user.id)
      .eq('following_id', userId)
      .limit(1);

    if (error) throw error;
    return data && data.length > 0;
  },

  async getFollowers(userId: string) {
    const { data, error } = await supabase
      .from('follows')
      .select(`
        *,
        follower:profiles!follows_follower_id_fkey (
          id,
          username,
          full_name,
          avatar_url,
          is_premium,
          user_type,
          art_categories
        )
      `)
      .eq('following_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(follow => follow.follower) || [];
  },

  async getFollowing(userId: string) {
    const { data, error } = await supabase
      .from('follows')
      .select(`
        *,
        following:profiles!follows_following_id_fkey (
          id,
          username,
          full_name,
          avatar_url,
          is_premium,
          user_type,
          art_categories
        )
      `)
      .eq('follower_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(follow => follow.following) || [];
  },

  async getFollowCounts(userId: string) {
    try {
      const [followersResult, followingResult] = await Promise.all([
        supabase.rpc('get_follower_count', { user_id: userId }),
        supabase.rpc('get_following_count', { user_id: userId })
      ]);

      if (followersResult.error) throw followersResult.error;
      if (followingResult.error) throw followingResult.error;

      return {
        followers: followersResult.data || 0,
        following: followingResult.data || 0
      };
    } catch (error) {
      console.error('Error getting follow counts:', error);
      return { followers: 0, following: 0 };
    }
  },

  async getMutualFollows(userId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    // Get users that both current user and target user follow
    const { data, error } = await supabase
      .from('follows')
      .select(`
        following_id,
        following:profiles!follows_following_id_fkey (
          id,
          username,
          full_name,
          avatar_url
        )
      `)
      .eq('follower_id', user.id)
      .in('following_id', [
        // Subquery to get users that target user follows
        supabase
          .from('follows')
          .select('following_id')
          .eq('follower_id', userId)
      ]);

    if (error) throw error;
    return data?.map(follow => follow.following) || [];
  },

  async getRecommendedUsers(limit: number = 5) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // First, get the IDs of users that current user is following
    const { data: followingData, error: followingError } = await supabase
      .from('follows')
      .select('following_id')
      .eq('follower_id', user.id);

    if (followingError) throw followingError;

    const followingIds = followingData?.map(f => f.following_id) || [];

    // Get users that current user is not following
    const query = supabase
      .from('profiles')
      .select('id, username, full_name, avatar_url, is_premium, user_type, art_categories')
      .eq('user_type', 'artist')
      .neq('id', user.id)
      .limit(limit);

    // Only add the not.in filter if there are actually users being followed
    if (followingIds.length > 0) {
      query.not('id', 'in', followingIds);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  }
};